import { page } from '$app/state';
import { browser } from '$app/environment';
import { replaceState } from '$app/navigation';

import type { ProductCollectionResult } from '../../types/collections';
import type { ProductLoader, CollectionStore, ContentState, LoadParams } from './collection-store.types';

import {
  clearFilterFacetsFromSearchParams,
  clearSelectedFilters,
  createInitialContentState,
  getSelectedFilters,
  getSortBy,
  getSortOption,
  getSortOptions,
  isFilteredOrSorted,
  mapFilters,
  setFilterFacetsToSearchParams
} from './collection-store.utils';

export function createCollectionStore(data: ProductCollectionResult = { items: [], facetFilters: [], count: 0, offset: 0, filtersSelected: {} }): CollectionStore {
  let loading = $state(false);
  let content = $state<ContentState>(createInitialContentState(data));
  let productLoader = $state<ProductLoader | undefined>();
  let filters = $derived(mapFilters(content.filtersAvailable));
  let sortOptions = $derived(getSortOptions());
  let defaultSortOption = $derived(sortOptions.find(option => option.value === 'relevance')!);

  console.log('afterNavigate:init:filters', content.filtersAvailable);

  const filterReferences = $state<CollectionStore['filterReferences']>({});
  let sortReference = $state<any>();

  const refresh = () => {
    Object.entries(content.filtersSelected).forEach(([ key, value ]) => {
      if (Array.isArray(value.values) ? value.values.length : value.values) {
        filterReferences[key].setValue(value.values);
      }
    });

    if (sortReference && content.sortBy) {
      const value = content.sortBy?.type === 'attribute' ? 'price_' + content.sortBy.order : content.sortBy?.type;
      sortReference.setValue([value]);
    }
  }

  const load = async (params: LoadParams = {}) => {
    if (!productLoader) {
      throw new Error('No product loader set');
    }

    const { facetFilter, sortBy = content.sortBy, incrementLoad = false } = params ?? {};

    if (facetFilter) {
      if (facetFilter.type === 'distinct') {
        content.filtersSelected[facetFilter.key] = { type: facetFilter.type, values: facetFilter.values };
      } else if (facetFilter.type === 'range') {
        content.filtersSelected[facetFilter.key] = { type: facetFilter.type, values: facetFilter.values };
      }
    }

    content.offset = incrementLoad ? content.offset + content.limit : 0;

    try {
      loading = true;


      const sort = getSortBy(sortBy);

      const updatedProducts = await productLoader(
        content.offset,
        content.limit,
        page.url,
        content.filtersSelected,
        sort
      );

      content.products = incrementLoad ? [...content.products, ...updatedProducts.items] : updatedProducts.items;
      content.filtersAvailable = updatedProducts.facetFilters;
      content.count = updatedProducts.count;
      content.offset = updatedProducts.offset;
      content.limit = updatedProducts.limit ?? content.limit;
      content.sortBy = getSortOption(updatedProducts.sortBy);

      if (browser) {
        replaceState(setFilterFacetsToSearchParams(page.url, content), page.state);
      }

      console.log('initated-content-filtersAvailable', $state.snapshot(content.filtersAvailable));
    } finally {
      loading = false;
    }
  };

  const reset = async () => {
    console.log('[collection-store.reset()]:');

    clearSelectedFilters(content);
    content.sortBy = undefined;

    await load({ offset: 0, limit: content.limit });
    Object.values(filterReferences).forEach((ref) => ref?.refresh());

    replaceState(clearFilterFacetsFromSearchParams(page.url), page.state);
  };

  return {
    get loading() { return loading; },
    get content() { return content; },
    get filters() { return Object.values(filters); },
    get sortOptions() {
      return {
        options: sortOptions,
        default: defaultSortOption
      }
    },
    set sortReference(value: any) { sortReference = value; },
    get sortReference() { return sortReference; },
    get productLoader(): ProductLoader | undefined { return productLoader; },
    set productLoader(value: ProductLoader) { productLoader = value; },
    get isFilteredOrSorted() { return isFilteredOrSorted(content, filters); },
    get isSorted() { return Boolean(content.sortBy); },
    filterReferences,
    refresh,
    load,
    reset,
    update: (products: ProductCollectionResult) => {
      clearSelectedFilters(content);

      content.products = products.items;
      content.count = products.count;
      content.offset = products.offset;
      content.limit = products.limit ?? content.limit;
      content.sortBy = getSortOption(products.sortBy);
      content.filtersAvailable = products.facetFilters;
      content.filtersSelected = getSelectedFilters(products);
      filters = mapFilters(products.facetFilters);

      Object.values(filterReferences).forEach((ref) => ref?.refresh());
    }
  };
}
